import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Form, Input, Button, Card, Select, Switch, Space,
    DatePicker, Typography, message, Spin, Upload
} from 'antd';
import {
    ArrowLeftOutlined, SaveOutlined, FileTextOutlined, PlusOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { newsAPI, uploadAPI } from '../services/api';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

export default function NewsForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [imageList, setImageList] = useState([]);
    const [uploading, setUploading] = useState(false);

    const fetchNews = useCallback(async () => {
        if (!id) return;

        try {
            setLoading(true);
            const response = await newsAPI.getById(id);
            const data = response.data;
            form.setFieldsValue({
                ...data,
                publishDate: data.publishDate ? dayjs(data.publishDate) : dayjs(),
                tags: data.tags?.join(', ') || ''
            });

            // Set image list if imageUrl exists
            if (data.imageUrl) {
                const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
                const baseURL = API_URL.replace('/api', '');
                const fullImageUrl = `${baseURL}${data.imageUrl}`;

                setImageList([{
                    uid: '-1',
                    name: 'image.jpg',
                    status: 'done',
                    url: fullImageUrl,
                    thumbUrl: fullImageUrl,
                }]);
            }
        } catch (error) {
            console.error('Error fetching news:', error);
            message.error('Failed to fetch article');
        } finally {
            setLoading(false);
        }
    }, [id, form]);

    useEffect(() => {
        if (isEdit) {
            fetchNews();
        }
    }, [isEdit, fetchNews]);

    const handleUpload = async ({ file, onSuccess, onError }) => {
        try {
            setUploading(true);
            const response = await uploadAPI.single(file);
            const imageUrl = response.data.file.url;

            // Create full URL for preview
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
            const baseURL = API_URL.replace('/api', '');
            const fullImageUrl = `${baseURL}${imageUrl}`;

            // Set imageUrl value in form (store relative path)
            form.setFieldsValue({ imageUrl });

            // Update image list with full URL for preview
            setImageList([{
                uid: file.uid,
                name: file.name,
                status: 'done',
                url: fullImageUrl,
                thumbUrl: fullImageUrl,
            }]);

            onSuccess(response.data);
            message.success('Image uploaded successfully');
        } catch (error) {
            console.error('Upload error:', error);
            onError(error);
            message.error('Failed to upload image');
        } finally {
            setUploading(false);
        }
    };

    const handleRemoveImage = () => {
        setImageList([]);
        form.setFieldsValue({ imageUrl: null });
    };

    const generateSlug = (title) => {
        return title
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');
    };

    const handleSubmit = async (values) => {
        setSubmitting(true);

        try {
            const dataToSubmit = {
                ...values,
                slug: values.slug || generateSlug(values.title),
                publishDate: values.publishDate?.toISOString(),
                tags: values.tags?.split(',').map(t => t.trim()).filter(Boolean) || []
            };

            if (isEdit) {
                await newsAPI.update(id, dataToSubmit);
                message.success('Article updated successfully!');
            } else {
                await newsAPI.create(dataToSubmit);
                message.success('Article created successfully!');
            }
            navigate('/news');
        } catch (error) {
            message.error('Error: ' + error.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" tip="Loading article..." />
            </div>
        );
    }

    return (
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
            <div style={{ marginBottom: 24 }}>
                <Button
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate('/news')}
                    style={{ marginBottom: 16 }}
                >
                    Back to Articles
                </Button>
                <Title level={2}>
                    <FileTextOutlined /> {isEdit ? 'Edit Article' : 'Add New Article'}
                </Title>
            </div>

            <Card style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)', borderRadius: '8px' }}>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{
                        category: 'news',
                        isPublished: true,
                        publishDate: dayjs()
                    }}
                >
                    <Form.Item
                        name="title"
                        label="Article Title"
                        rules={[{ required: true, message: 'Title is required!' }]}
                    >
                        <Input size="large" placeholder="Enter article title" />
                    </Form.Item>

                    <Form.Item name="slug" label="Slug">
                        <Input size="large" placeholder="auto-generated-if-empty" />
                    </Form.Item>

                    <Form.Item
                        name="excerpt"
                        label="Excerpt"
                        rules={[{ required: true, message: 'Excerpt is required!' }]}
                    >
                        <TextArea rows={3} placeholder="Brief summary of the article" />
                    </Form.Item>

                    <Form.Item
                        name="content"
                        label="Content"
                        rules={[{ required: true, message: 'Content is required!' }]}
                    >
                        <TextArea rows={10} placeholder="Full article content" />
                    </Form.Item>

                    {/* Hidden field for imageUrl */}
                    <Form.Item name="imageUrl" hidden>
                        <Input />
                    </Form.Item>

                    {/* Upload Image */}
                    <Form.Item label="Article Image">
                        <Upload
                            listType="picture-card"
                            fileList={imageList}
                            customRequest={handleUpload}
                            onRemove={handleRemoveImage}
                            maxCount={1}
                            accept="image/*"
                            disabled={uploading}
                        >
                            {imageList.length === 0 && (
                                <div>
                                    <PlusOutlined />
                                    <div style={{ marginTop: 8 }}>
                                        {uploading ? 'Uploading...' : 'Upload'}
                                    </div>
                                </div>
                            )}
                        </Upload>
                        <div style={{ color: '#666', fontSize: '12px', marginTop: '8px' }}>
                            Recommended size: 1200x800px. Max file size: 5MB
                        </div>
                    </Form.Item>

                    <Form.Item name="author" label="Author">
                        <Input size="large" placeholder="Author name" />
                    </Form.Item>

                    <Form.Item name="category" label="Category">
                        <Select size="large">
                            <Option value="news">News</Option>
                            <Option value="project">Project</Option>
                            <Option value="technology">Technology</Option>
                            <Option value="regulation">Regulation</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item name="tags" label="Tags" tooltip="Comma-separated tags">
                        <Input size="large" placeholder="engineering, structure, design" />
                    </Form.Item>

                    <Form.Item name="publishDate" label="Publish Date">
                        <DatePicker size="large" style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item name="isPublished" label="Status" valuePropName="checked">
                        <Switch checkedChildren="Published" unCheckedChildren="Draft" />
                    </Form.Item>

                    <Form.Item style={{ marginTop: 32 }}>
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                            <Button
                                size="large"
                                onClick={() => navigate('/news')}
                                style={{ minWidth: 120 }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                size="large"
                                htmlType="submit"
                                icon={<SaveOutlined />}
                                loading={submitting}
                                style={{
                                    minWidth: 120,
                                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                                    border: 'none',
                                }}
                            >
                                {isEdit ? 'Update' : 'Publish'} Article
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
}
